import type { ReactNode } from "react";

import { OndoMark } from "../lib/OndoMark.tsx";
import { WorkIdentity } from "../work/WorkIdentity.tsx";
import { ROOM_COPY } from "./room.constants.ts";
import type { ChainLinkView, RoomCardView, RoomHead, RoomWorkView } from "./room.model.ts";

/**
 * The chrome around a viewing room.
 *
 * Deliberately not `AuthShell`: that frame is about getting into ONDO, and this
 * page is for somebody who has no reason to. The mark is here because the room
 * is hosted by the registry and a visitor should be able to tell whose page they
 * are on; nothing on it invites them to sign up.
 */
export function RoomFrame({ head, children }: { head: RoomHead | null; children: ReactNode }) {
  return (
    <main className="room">
      <div className="room__frame">
        <span className="room__brand" aria-hidden="true">
          <OndoMark />
        </span>

        {head && (
          <header className="room__head">
            <h1 className="room__title">{head.title}</h1>
            <p className="room__by">{head.by}</p>
            {head.subtitle && <p className="room__subtitle">{head.subtitle}</p>}
            {head.count && <p className="room__count">{head.count}</p>}
            {head.message && <p className="room__message">{head.message}</p>}
          </header>
        )}

        {children}
      </div>
    </main>
  );
}

/**
 * One field, one button — used for both the email and the password.
 *
 * One component because the two are the same act with different words, and two
 * would be two places to get the `autoComplete` and the `role="alert"` right.
 */
export function RoomGate({
  lead,
  label,
  action,
  value,
  problem,
  busy,
  secret,
  onType,
  onSubmit,
}: {
  lead: string;
  label: string;
  action: string;
  value: string;
  problem: string | null;
  busy: boolean;
  secret: boolean;
  onType: (value: string) => void;
  onSubmit: () => void;
}) {
  return (
    <form
      className="room__gate"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <p className="room__gate-lead">{lead}</p>

      <label className="room__label" htmlFor="room-gate-field">
        {label}
      </label>
      <input
        id="room-gate-field"
        className="room__input"
        type={secret ? "password" : "email"}
        inputMode={secret ? undefined : "email"}
        autoComplete={secret ? "off" : "email"}
        value={value}
        onChange={(event) => onType(event.target.value)}
      />

      {problem && (
        <p className="room__problem" role="alert">
          {problem}
        </p>
      )}

      <button type="submit" className="room__submit" disabled={busy}>
        {action}
      </button>
    </form>
  );
}

/**
 * A work, as a card in a room.
 *
 * Forked from `WorkCardView` rather than reused, and the fork is the point: that
 * card's foot is the collection, the holder and the location — the three facts a
 * viewing room exists to withhold. A card whose only difference is the absence
 * of its only distinguishing block is a different card.
 */
export function RoomWorkCard({ card, onOpen }: { card: RoomCardView; onOpen: () => void }) {
  return (
    <li className="room__card">
      {/* A button, not a link: opening a work is a state change inside this
          page. There is no second URL, because cold-loading one would land on
          the gate anyway. */}
      <button type="button" className="room__card-button" onClick={onOpen}>
        <span className="room__card-thumb">
          {card.imageUrl === null ? (
            <span className="room__card-blank">{ROOM_COPY.noImage}</span>
          ) : (
            <img className="room__card-img" src={card.imageUrl} alt={card.alt} />
          )}
        </span>

        <span className="room__card-body">
          <span className="room__card-title">{card.title}</span>
          <span className="room__card-attribution">{card.attribution}</span>
          {card.medium && <span className="room__card-medium">{card.medium}</span>}
        </span>
      </button>
    </li>
  );
}

/**
 * The chain of title, read-only.
 *
 * Forked from `ProvenanceEntryList` for a practical reason rather than a design
 * one: that component requires eleven write handlers and a surface union with no
 * "hidden" for documents, so a room surface would be eleven no-ops in front of a
 * list that has no controls on it. `METHOD_EVENTS` is shared, so a link reads
 * the same here as it does on the collector's own screen.
 */
export function RoomChain({ links }: { links: ChainLinkView[] }) {
  if (links.length === 0) {
    return <p className="room__note">{ROOM_COPY.chainEmpty}</p>;
  }

  return (
    <section className="room__chain">
      <h2 className="room__chain-title">{ROOM_COPY.chainTitle}</h2>
      <p className="room__note">{ROOM_COPY.chainHelp}</p>

      <ol className="room__chain-list">
        {links.map((link) => (
          <li key={link.key} className="room__chain-link">
            <span
              className={`room__chain-glyph room__chain-glyph--${link.tone}`}
              aria-hidden="true"
            >
              {link.glyph}
            </span>
            <span className="room__chain-body">
              {/* Null on an auction the record does not name a buyer for. The
                  action line below carries the fact either way, so an absent
                  owner leaves a shorter link rather than an empty one. */}
              {link.owner !== null && (
                <span className="room__chain-owner">{link.owner}</span>
              )}
              <span className="room__chain-meta">
                {link.action}
                {link.when ? ` · ${link.when}` : ""}
              </span>
              {link.where && <span className="room__chain-where">{link.where}</span>}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** One work, opened. */
export function RoomWorkPanel({
  work,
  backLabel,
  onBack,
}: {
  work: RoomWorkView;
  backLabel: string;
  onBack: () => void;
}) {
  return (
    <section className="room__detail">
      <button type="button" className="room__back" onClick={onBack}>
        {backLabel}
      </button>

      <div className="room__detail-columns">
        <div className="room__object">
          {work.hero === null ? (
            <div className="room__hero room__hero--blank">{ROOM_COPY.noImage}</div>
          ) : (
            <img className="room__hero" src={work.hero.url} alt={work.hero.alt} />
          )}

          {work.images.length > 1 && (
            <ul className="room__strip">
              {work.images.slice(1).map((image) => (
                <li key={image.url}>
                  <img className="room__strip-img" src={image.url} alt={image.alt} />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="room__facts">
          {/* Reused verbatim from the work record. It renders a title, an
              attribution, an optional badge and a list of rows — and the rows a
              room builds cannot name a collection, a holder or a location,
              because `placementRows` takes a PlacedWork and a room has none. */}
          <WorkIdentity identity={work.identity} />

          {work.description && <p className="room__description">{work.description}</p>}

          {work.chain && <RoomChain links={work.chain} />}
        </div>
      </div>
    </section>
  );
}
